import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL, WS_URL, apiFetch } from '../config/api';
import { DEFAULT_TELEMETRY } from '../config/constants';
import { logger } from '../utils/logger';
import { getGlobalErrorLogger } from '../contexts/ErrorLogContext';

/**
 * Shallow-compare two telemetry objects on key flight fields.
 * Returns true if they are "the same" for display purposes.
 */
function telemetryEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    return (
        a.connected === b.connected &&
        a.lat === b.lat &&
        a.lon === b.lon &&
        a.alt === b.alt &&
        a.alt_msl === b.alt_msl &&
        a.relative_alt === b.relative_alt &&
        a.alt_raw === b.alt_raw &&
        a.relative_alt_raw === b.relative_alt_raw &&
        a.heading === b.heading &&
        a.groundspeed === b.groundspeed &&
        a.airspeed === b.airspeed &&
        a.mode === b.mode &&
        a.armed === b.armed &&
        a.battery_remaining === b.battery_remaining &&
        a.battery_voltage === b.battery_voltage &&
        a.battery_current === b.battery_current &&
        a.satellites === b.satellites &&
        a.gps_fix === b.gps_fix &&
        a.roll === b.roll &&
        a.pitch === b.pitch &&
        a.yaw === b.yaw &&
        a.throttle === b.throttle &&
        a.climbRate === b.climbRate &&
        a.wpDist === b.wpDist &&
        // Airspace-claim state. Must be compared here: a drone can go
        // Nonconforming on a frame where every flight field is byte-identical,
        // and without this the dedupe would swallow the one update that matters.
        a.intentState === b.intentState &&
        alertsKey(a) === alertsKey(b)
    );
}

// Alerts change without any flight field changing (a breach is declared on the
// third deviating tick), so they take part in the dedupe too.
function alertsKey(t) {
    const list = t && t.serverAlerts;
    return Array.isArray(list) ? list.map((x) => `${x.level}|${x.category}|${x.message}`).join('~') : '';
}

/**
 * Custom hook for managing telemetry data via WebSocket and HTTP polling
 * Uses a SINGLE WebSocket connection and deduplicates all state updates
 * to prevent "Maximum update depth exceeded" errors.
 * 
 * Now also extracts activeMission data from WS broadcasts so all pages
 * (Map, Missions, Drones) can react to mission state changes in real-time.
 */
export function useTelemetry(activeSection, selectedDroneId) {
    const [telemetry, setTelemetryRaw] = useState(DEFAULT_TELEMETRY);
    const [dronesTelemetry, setDronesTelemetryRaw] = useState({});
    const [activeMissions, setActiveMissionsRaw] = useState({}); // droneId -> activeMission
    const [connected, setConnected] = useState(false);

    // Refs to avoid stale closures — effects read from refs, not deps
    const selectedDroneIdRef = useRef(selectedDroneId);
    const activeSectionRef = useRef(activeSection);

    useEffect(() => { selectedDroneIdRef.current = selectedDroneId; }, [selectedDroneId]);
    useEffect(() => { activeSectionRef.current = activeSection; }, [activeSection]);

    // ---- Deduplicating setters (never produce a new object if values are same) ----
    const setTelemetrySafe = useCallback((newTel) => {
        setTelemetryRaw(prev => {
            const merged = typeof newTel === 'function' ? newTel(prev) : newTel;
            if (merged === prev || telemetryEqual(prev, merged)) return prev;
            return merged;
        });
    }, []);

    // fullBatch: `updates` is the whole fleet, so another operator's aircraft
    // missing from it has landed or gone and is dropped.
    const updateDronesTelemetry = useCallback((updates, fullBatch = false) => {
        setDronesTelemetryRaw(prev => {
            const gone = fullBatch
                ? Object.keys(prev).filter((id) => prev[id] && prev[id].readonly && !updates[id])
                : [];
            let changed = gone.length > 0;
            for (const id of Object.keys(updates)) {
                if (changed) break;
                if (!telemetryEqual(prev[id], updates[id])) changed = true;
            }
            if (!changed) return prev;
            const next = { ...prev, ...updates };
            gone.forEach((id) => { delete next[id]; });
            return next;
        });
    }, []);

    const updateActiveMissions = useCallback((updates) => {
        setActiveMissionsRaw(prev => {
            let changed = false;
            for (const id of Object.keys(updates)) {
                const prevM = prev[id];
                const newM = updates[id];
                // Compare by state + progress + current_waypoint
                if (
                    prevM?.state !== newM?.state ||
                    prevM?.progress !== newM?.progress ||
                    prevM?.currentWaypoint !== newM?.currentWaypoint ||
                    prevM?.missionId !== newM?.missionId ||
                    prevM?.durationSec !== newM?.durationSec ||
                    prevM?.startedAt !== newM?.startedAt
                ) {
                    changed = true;
                    break;
                }
            }
            if (!changed) return prev;
            return { ...prev, ...updates };
        });
    }, []);

    const resetTelemetry = useCallback(() => {
        setTelemetryRaw(DEFAULT_TELEMETRY);
    }, []);

    const handleConnectionStatus = useCallback((status) => {
        setConnected(status);
        if (!status) resetTelemetry();
    }, [resetTelemetry]);

    // ================================================================
    // SINGLE WebSocket — stays open for the lifetime of the hook.
    // Reads section/droneId from refs so deps never change.
    // ================================================================
    const wsRef = useRef(null);

    const getTokenSafe = useCallback(() => {
        try {
            return localStorage.getItem('utms_token');
        } catch {
            return null;
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        let reconnectTimer = null;
        let reconnectDelay = 1000; // Start at 1s, exponential backoff up to 30s
        const MAX_RECONNECT_DELAY = 30000;

        function openWS() {
            if (!isMounted) return;
            // Pass auth token via query string for user-scoped WebSocket
            const token = getTokenSafe();
            const separator = WS_URL.includes('?') ? '&' : '?';
            const wsUrl = token ? `${WS_URL}${separator}token=${encodeURIComponent(token)}` : WS_URL;

            let ws;
            try {
                ws = new WebSocket(wsUrl);
            } catch (error) {
                setConnected(false);
                if (isMounted) {
                    reconnectTimer = setTimeout(openWS, reconnectDelay);
                    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
                }
                logger.error('WebSocket initialization failed:', error);
                getGlobalErrorLogger().error('WS', `WebSocket init failed: ${error.message}`, { wsUrl: WS_URL });
                return;
            }

            wsRef.current = ws;

            ws.onopen = () => {
                reconnectDelay = 1000; // Reset on successful connection
                setConnected(true);
                const droneId = selectedDroneIdRef.current;
                const section = activeSectionRef.current;
                if (section === 'Drones' && droneId) {
                    logger.log('✅ WebSocket connected for drone:', droneId);
                    ws.send(JSON.stringify({ type: 'subscribe', droneId }));
                } else {
                    logger.log('✅ Global WebSocket connected');
                    ws.send(JSON.stringify({ type: 'subscribe_all' }));
                }
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (!data || typeof data !== 'object') return;
                    // A mission/drone/delivery/config changed — maybe on another
                    // screen of this account (backend services/syncEvents.js).
                    // Pages listening for this refetch what they show.
                    if (data.type === 'entity_changed') {
                        window.dispatchEvent(new CustomEvent('utms:entity-changed', { detail: data }));
                        return;
                    }
                    const droneId = selectedDroneIdRef.current;

                    if (data.type === 'telemetry_batch' && Array.isArray(data.drones)) {
                        const batch = {};
                        const missionBatch = {};
                        data.drones.filter(Boolean).forEach(d => {
                            if (!d || !d.id) return;
                            batch[d.id] = {
                                ...(d.telemetry || {}),
                                readonly: !!d.readonly,
                                droneName: d.name || d.id,
                                vehicleModel: d.vehicleModel || (d.telemetry && d.telemetry.vehicleModel) || 'quadcopter',
                                intentState: d.intentState || null,
                                // The server's own alerts for this drone (corridor breach,
                                // per-drone battery thresholds, altitude fence, stale
                                // telemetry, …). Used to be dropped here, so no operator
                                // ever saw them.
                                serverAlerts: Array.isArray(d.alerts) ? d.alerts : [],
                            };
                            if (d.activeMission !== undefined) {
                                missionBatch[d.id] = d.activeMission;
                            }
                        });
                        updateDronesTelemetry(batch, true);
                        if (Object.keys(missionBatch).length > 0) {
                            updateActiveMissions(missionBatch);
                        }

                        if (droneId && batch[droneId]) {
                            setTelemetrySafe(batch[droneId]);
                        } else if (!droneId) {
                            const firstId = Object.keys(batch)[0];
                            if (firstId) setTelemetrySafe(batch[firstId]);
                        }
                    } else if (data && data.droneId) {
                        const telData = {
                            ...(data.telemetry || data),
                            readonly: !!data.readonly,
                            droneName: data.name || data.droneId || (data.telemetry && data.telemetry.droneName),
                            vehicleModel: data.vehicleModel || (data.telemetry && data.telemetry.vehicleModel) || 'quadcopter',
                            intentState: data.intentState || null,
                            // The Drones page streams one drone, and its Alerts
                            // panel is the one place server alerts are listed.
                            serverAlerts: Array.isArray(data.alerts) ? data.alerts : [],
                        };
                        updateDronesTelemetry({ [data.droneId]: telData });
                        if (data.activeMission !== undefined) {
                            updateActiveMissions({ [data.droneId]: data.activeMission });
                        }
                        if (!droneId || data.droneId === droneId) {
                            setTelemetrySafe(telData);
                        }
                    }
                } catch (_) { /* silent */ }
            };

            ws.onclose = (event) => {
                setConnected(false);
                if (isMounted) {
                    getGlobalErrorLogger().warn('WS', `WebSocket closed (code: ${event.code}). Reconnecting in ${reconnectDelay / 1000}s...`, {
                        code: event.code,
                        reason: event.reason || '',
                        reconnectDelay,
                    });
                    reconnectTimer = setTimeout(openWS, reconnectDelay);
                    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
                }
            };
            ws.onerror = (event) => {
                setConnected(false);
                getGlobalErrorLogger().error('WS', 'WebSocket error occurred', { wsUrl: WS_URL });
                /* onclose fires next */
            };
        }

        openWS();

        return () => {
            isMounted = false;
            clearTimeout(reconnectTimer);
            const ws = wsRef.current;
            if (ws) {
                ws.onclose = null; // prevent reconnect on intentional close
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                    ws.close();
                }
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setConnected, getTokenSafe]); // single WS for whole app lifetime

    // When user switches drone / section — re-subscribe on existing WS
    useEffect(() => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;

        if (activeSection === 'Drones' && selectedDroneId) {
            ws.send(JSON.stringify({ type: 'subscribe', droneId: selectedDroneId }));
        } else {
            ws.send(JSON.stringify({ type: 'subscribe_all' }));
        }
    }, [activeSection, selectedDroneId]);

    // ================================================================
    // Slow HTTP fallback (every 5s) — only fires if WS is dead
    // Delayed start to give WS time to connect first
    // ================================================================
    useEffect(() => {
        const fetchFallback = async () => {
            // Skip if WS is alive
            const ws = wsRef.current;
            if (ws && ws.readyState === WebSocket.OPEN) return;

            try {
                const droneId = selectedDroneIdRef.current;
                if (activeSectionRef.current === 'Drones' && droneId) {
                    const res = await apiFetch(`${API_URL}/drones/${droneId}`);
                    if (res.ok) {
                        const data = await res.json();
                        if (data.success && data.drone && data.drone.telemetry) {
                            const tel = data.drone.telemetry;
                            updateDronesTelemetry({ [droneId]: tel });
                            setTelemetrySafe(tel);
                        }
                    }
                } else {
                    const res = await apiFetch(`${API_URL}/drones`);
                    if (res.ok) {
                        const data = await res.json();
                        if (data.success && data.drones) {
                            const batch = {};
                            data.drones.forEach((d) => {
                                if (d?.id && d?.telemetry) {
                                    batch[d.id] = d.telemetry;
                                }
                            });
                            if (Object.keys(batch).length > 0) {
                                updateDronesTelemetry(batch);
                            }

                            const selectedId = selectedDroneIdRef.current;
                            if (selectedId && batch[selectedId]) {
                                setTelemetrySafe(batch[selectedId]);
                            } else {
                                const connectedDrone = data.drones.find((d) => d?.telemetry?.connected);
                                if (connectedDrone?.telemetry) {
                                    setTelemetrySafe(connectedDrone.telemetry);
                                }
                            }
                        }
                    }
                }
            } catch (_) { /* silent */ }
        };

        // Delay first call by 2s to give WS time to connect
        const startTimer = setTimeout(() => {
            fetchFallback();
        }, 2000);
        const interval = setInterval(fetchFallback, 5000);
        return () => { clearTimeout(startTimer); clearInterval(interval); };
    }, [setTelemetrySafe, updateDronesTelemetry]);

    return {
        telemetry,
        dronesTelemetry,
        activeMissions,
        setTelemetry: setTelemetrySafe,
        connected,
        handleConnectionStatus,
        resetTelemetry,
    };
}

export default useTelemetry;
